import * as React from 'react';
import styled from 'styled-components';

import Button from 'src/SharedComponents/Button';
import Dialog from 'src/SharedComponents/Dialog/Dialog';
import DialogActions from 'src/SharedComponents/Dialog/DialogActions';
import ErrorBox from 'src/SharedComponents/ErrorBox';
import LoadingIndicator from 'src/SharedComponents/LoadingIndicator';
import TextInput from 'src/SharedComponents/TextInput';
import ManagerTitleBar from 'src/Views/Manager/ManagerTitleBar';

import IRole from 'src/ServerEntities/IRole';

const UserFieldsContainer = styled.div`
    display: flex;
    flex-direction: column;
    padding: 16px;
`;

const UserFieldLine = styled.div`
    display: flex;
    flex-direction: row;
    align-items: flex-start;
`;

const UserFieldCell = styled.div`
    width: 100%;
`;

const StatusContainer = styled.div`
    height: 50px;
    text-align: center;
`;

const MainContainer = styled.div`
    display: flex;
    height: 100%;
    flex-direction: column;
    flex-wrap: nowrap;
    align-content: space-around;
    justify-content: center;
    align-items: center;
`;

interface IProps {
    show: boolean;
    role: IRole;
    onClose: () => void;
    onSave: (displayName: string, description: string) => void;
    loading?: boolean;
    errorMessage?: string;
    success?: boolean;
}

const EditRoleDialog = (props: IProps) => {
    const { role, show, onClose, onSave, loading, errorMessage, success } = props;
    
    const [displayName, setDisplayName] = React.useState(role.displayName || role.id);
    const [description, setDescription] = React.useState(role.description);
    const [error, setError] = React.useState("");

    React.useEffect(() => {
        setDisplayName(role.displayName || role.id);
        setDescription(role.description);
    }, [role]);

    const handleSave = () => {
        setError("");
        let valid = true;
        
        if (!displayName || !description) {
            setError("Display name and description cannot be empty.");
            valid = false;
        }

        if (!valid) return;

        onSave(displayName, description);
    };

    const closeDialog = () => {
        setDisplayName(role.displayName || role.id);
        setDescription(role.description);
        setError("");
        onClose();
    };

    const changeDisplayName = (e: React.SyntheticEvent<HTMLInputElement>) => {
        setDisplayName(e.currentTarget.value);
        if (e.currentTarget.value) setError("");
    };

    const changeDescription = (e: React.SyntheticEvent<HTMLInputElement>) => {
        setDescription(e.currentTarget.value);
        if (e.currentTarget.value) setError("");
    };

    return (
        <Dialog open={show} onClose={closeDialog} style={{ width: "650px", height: "450px" }} darkBackground={true}>
            <ManagerTitleBar viewName="Edit role" viewDescription={`Edit display name and description for role: ${role.id}`} />
            <LoadingIndicator show={loading || false} type="Linear" />
            
            {!success && (
                <UserFieldsContainer>
                    <UserFieldLine>
                        <UserFieldCell>
                            <TextInput 
                                margin="10px 10px 20px 10px" 
                                value={role.id} 
                                onChange={() => {}} 
                                label="Role ID (cannot be changed)" 
                                disabled 
                            />
                        </UserFieldCell>
                    </UserFieldLine>

                    <UserFieldLine>
                        <UserFieldCell>
                            <TextInput 
                                margin="10px 10px 20px 10px" 
                                value={displayName} 
                                onChange={changeDisplayName} 
                                label="Display Name" 
                            />
                        </UserFieldCell>
                    </UserFieldLine>

                    <UserFieldLine>
                        <UserFieldCell>
                            <TextInput 
                                margin="10px 10px 20px 10px" 
                                value={description} 
                                onChange={changeDescription} 
                                label="Description" 
                            />
                        </UserFieldCell>
                    </UserFieldLine>

                    <StatusContainer>
                        {(error || errorMessage) && <ErrorBox>{error || errorMessage}</ErrorBox>}
                    </StatusContainer>

                    <DialogActions>
                        <Button outlined={true} onClick={closeDialog}>Cancel</Button>
                        <Button onClick={handleSave} margin="0px 16px 0px 16px">Save</Button>
                    </DialogActions>
                </UserFieldsContainer>
            )}
            
            {success && (
                <MainContainer>
                    <StatusContainer>Role updated successfully</StatusContainer>
                    <Button onClick={closeDialog} margin="0px 16px 0px 16px">Close</Button>
                </MainContainer>
            )}
        </Dialog>
    );
};

export default EditRoleDialog;